/**
 * Feb 2026 · Public form renderer.
 *
 * Standalone page for a published form. No app shell, no auth required.
 * Also works inside an iframe or the acq-embed.js loader.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";

// Feb 2026 · Evaluate a field's visible_if rules against the current
// form values. Returns true when the field should be shown.
function evalVisibleIf(field, values) {
  const rules = field?.visible_if || [];
  if (rules.length === 0) return true;
  const match = field.visible_if_match || "all";
  const check = (r) => {
    const v = values[r.field_id];
    const s = v == null ? "" : String(v);
    const target = r.value == null ? "" : String(r.value);
    switch (r.op) {
      case "eq":         return s === target;
      case "ne":         return s !== target;
      case "contains":   return s.toLowerCase().includes(target.toLowerCase());
      case "gt":         return Number(s) > Number(target);
      case "lt":         return Number(s) < Number(target);
      case "empty":      return s === "";
      case "not_empty":  return s !== "";
      case "in":         return target.split(",").map((t) => t.trim()).includes(s);
      case "not_in":     return !target.split(",").map((t) => t.trim()).includes(s);
      default:           return true;
    }
  };
  return match === "any" ? rules.some(check) : rules.every(check);
}



const FONT_MAP = {
  system: "system-ui,-apple-system,'Segoe UI',sans-serif",
  inter:  "'Inter','Helvetica Neue',Arial,sans-serif",
  serif:  "Georgia,'Times New Roman',serif",
  mono:   "'Courier New',Menlo,monospace",
};

export default function PublicForm() {
  const { formId } = useParams();
  const [params] = useSearchParams();
  const [form, setForm] = useState(null);
  const [state, setState] = useState({ loading: true, error: null, submitted: false, submitting: false });
  const [values, setValues] = useState({});
  const [captchaReady, setCaptchaReady] = useState(false);
  const captchaScript = useRef(null);

  // Fetch form schema
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/forms/${formId}`);
        setForm(data);
        // Prime hidden fields from URL params.
        const seed = {};
        (data.fields || []).forEach((f) => {
          if (f.kind === "hidden") {
            const src = f.hidden_source || f.label;
            seed[f.id] = params.get(src) || f.default_value || "";
          } else if (f.default_value) {
            seed[f.id] = f.default_value;
          }
        });
        setValues(seed);
      } catch (e) {
        setState((s) => ({ ...s, loading: false, error: e?.response?.status === 404 ? "Form not found or not published." : "Something went wrong loading this form." }));
        return;
      }
      setState((s) => ({ ...s, loading: false }));
    })();
    // eslint-disable-next-line
  }, [formId]);

  // Load reCAPTCHA v3 if configured
  useEffect(() => {
    const key = form?.config?.recaptcha_site_key;
    if (!key || captchaScript.current) return;
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(key)}`;
    s.async = true;
    s.onload = () => setCaptchaReady(true);
    document.head.appendChild(s);
    captchaScript.current = s;
  }, [form]);

  // Feb 2026 · When embedded via acq-embed.js, tell the parent frame our
  // real content height so the iframe can resize itself. This uses
  // ResizeObserver on <body> so it fires on any layout change (adding
  // fields, showing errors, etc).
  useEffect(() => {
    if (typeof window === "undefined" || window === window.parent) return;
    const post = () => {
      const h = Math.min(2400, Math.max(200, document.body.scrollHeight));
      try { window.parent.postMessage({ type: "acq-form-height", height: h }, "*"); } catch { /* noop */ }
    };
    post();
    const ro = new ResizeObserver(() => post());
    ro.observe(document.body);
    return () => ro.disconnect();
  }, [form, state.submitted]);

  // Compose theme CSS variables + custom CSS
  const styleVars = useMemo(() => {
    const s = form?.style || {};
    return {
      "--acq-primary":    s.primary_color || "#0B2545",
      "--acq-bg":         s.background_color || "#FFFFFF",
      "--acq-text":       s.text_color || "#12233F",
      "--acq-accent":     s.accent_color || "#C9A227",
      "--acq-radius":     `${s.corner_radius ?? 6}px`,
      "--acq-fontsize":   `${s.font_size ?? 14}px`,
      "--acq-font":       FONT_MAP[s.font_family || "system"],
    };
  }, [form]);

  const onChange = (fid, v) => setValues((prev) => ({ ...prev, [fid]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (state.submitting) return;
    setState((s) => ({ ...s, submitting: true, error: null }));
    try {
      // reCAPTCHA v3
      let recaptcha_token = null;
      const siteKey = form?.config?.recaptcha_site_key;
      if (siteKey && window.grecaptcha && captchaReady) {
        recaptcha_token = await new Promise((resolve) => {
          window.grecaptcha.ready(() => {
            window.grecaptcha.execute(siteKey, { action: "submit" }).then(resolve).catch(() => resolve(null));
          });
        });
      }
      // UTM harvest from URL
      const utm = {};
      ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
        const v = params.get(k); if (v) utm[k] = v;
      });
      // Honeypot value
      const honeypot = document.getElementById("acq-honey")?.value || "";
      const { data } = await api.post(`/public/forms/${formId}/submit`, {
        values,
        honeypot,
        recaptcha_token,
        referrer: document.referrer || null,
        utm,
      });
      // Redirect if configured (respect optional delay).
      // Feb 2026 · Always give the thank-you screen a beat to render
      // before navigating away — an immediate redirect meant customers
      // never saw the confirmation, and admins reported "thank-you
      // didn't show" even though the message was configured. Minimum
      // 2 s dwell if a redirect_url is set; admin can lengthen via
      // `redirect_delay_seconds`.
      if (data?.redirect_url) {
        const configured = Math.max(0, Math.min(60, Number(form?.config?.redirect_delay_seconds) || 0));
        const delay = configured > 0 ? configured : 2;
        setState({ loading: false, error: null, submitted: true, submitting: false, redirectIn: delay });
        // `redirect_target_top` defaults to TRUE — when the form is
        // embedded in an iframe/popup, send the top window to the URL
        // instead of reloading just the embed. The second branch keeps
        // the thank-you inside the embed for admins who prefer that.
        //
        // Cross-origin iframes can WRITE to window.top.location.href
        // (user-activated form submit counts as a navigation gesture)
        // but can't READ it. We also broadcast a postMessage so a
        // parent that has its own listener can intercept.
        const topRedirect = form?.config?.redirect_target_top !== false;
        setTimeout(() => {
          try {
            window.parent && window.parent.postMessage(
              { type: "acq-form-redirect", form_id: formId, url: data.redirect_url, target: topRedirect ? "top" : "self" },
              "*",
            );
          } catch { /* noop */ }
          try {
            if (topRedirect && window.top && window.top !== window.self) {
              // eslint-disable-next-line no-restricted-globals
              window.top.location.href = data.redirect_url;
            } else {
              window.location.href = data.redirect_url;
            }
          } catch {
            // If a cross-origin frame refused the top navigation, fall
            // back to the embed location so the user still gets there.
            window.location.href = data.redirect_url;
          }
        }, delay * 1000);
        return;
      }
      setState({ loading: false, error: null, submitted: true, submitting: false });
      try { window.parent && window.parent.postMessage({ type: "acq-form-submitted", form_id: formId }, "*"); } catch { /* noop */ }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please check your entries.";
      setState({ loading: false, error: msg, submitted: false, submitting: false });
    }
  };

  if (state.loading) {
    return <div style={{ padding: 40, textAlign: "center", fontFamily: FONT_MAP.system, color: "#6B7280" }}>Loading…</div>;
  }
  if (state.error && !form) {
    return <div style={{ padding: 40, textAlign: "center", fontFamily: FONT_MAP.system, color: "#DC2626" }}>{state.error}</div>;
  }
  if (state.submitted) {
    const cfg = form?.config || {};
    const tyHtml = cfg.thank_you_html;
    const shareUrl = typeof window !== "undefined" ? window.location.href : "";
    const share = (net) => {
      const encoded = encodeURIComponent(shareUrl);
      const map = {
        twitter: `https://twitter.com/intent/tweet?url=${encoded}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`,
        whatsapp: `https://wa.me/?text=${encoded}`,
      };
      window.open(map[net], "_blank", "noopener");
    };
    return (
      <div style={{ ...styleVars, background: "var(--acq-bg)", color: "var(--acq-text)", minHeight: "100vh", padding: 40, fontFamily: "var(--acq-font)", fontSize: "var(--acq-fontsize)" }}>
        {cfg.thank_you_show_confetti && <ConfettiBurst color={s.primary_color || "#EA5934"} />}
        <div className="acq-form-card" style={{ maxWidth: 640, margin: "0 auto", background: "var(--acq-bg)", borderRadius: "var(--acq-radius)", padding: 20, textAlign: "center", position: "relative", zIndex: 2 }} data-testid="public-form-thankyou">
          {cfg.thank_you_image_url ? (
            <img src={cfg.thank_you_image_url} alt="" style={{ maxWidth: 260, margin: "0 auto 20px", display: "block" }} />
          ) : (
            <div style={{ fontSize: 40, color: "var(--acq-primary)", marginBottom: 12 }}>✓</div>
          )}
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Thanks — we got it.</div>
          {tyHtml ? <div dangerouslySetInnerHTML={{ __html: tyHtml }} /> : <div style={{ opacity: 0.75 }}>We'll be in touch shortly.</div>}
          {state.redirectIn > 0 && (
            <div style={{ marginTop: 16, opacity: 0.6, fontSize: 12 }}>
              Redirecting in {state.redirectIn}s…
            </div>
          )}
          {cfg.thank_you_cta_url && cfg.thank_you_cta_label && (
            <a href={cfg.thank_you_cta_url} target="_blank" rel="noopener noreferrer"
                data-testid="public-form-cta"
                style={{ display: "inline-block", marginTop: 20, padding: "10px 20px",
                          background: "var(--acq-primary)", color: "#fff",
                          textDecoration: "none", borderRadius: "var(--acq-radius)",
                          fontWeight: 600 }}>
              {cfg.thank_you_cta_label} →
            </a>
          )}
          {cfg.thank_you_show_share && (
            <div style={{ marginTop: 20, display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap" }} data-testid="public-form-share">
              {["twitter", "linkedin", "whatsapp"].map((n) => (
                <button key={n} onClick={() => share(n)}
                         style={{ padding: "8px 14px", background: "transparent", color: "var(--acq-primary)",
                                   border: "1px solid var(--acq-primary)", cursor: "pointer",
                                   textTransform: "uppercase", fontSize: 11, letterSpacing: 1 }}>
                  Share on {n}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const s = form.style || {};
  const cfg = form.config || {};

  return (
    <div style={{ ...styleVars, background: "var(--acq-bg)", color: "var(--acq-text)", minHeight: "100vh", padding: "16px 12px", fontFamily: "var(--acq-font)", fontSize: "var(--acq-fontsize)" }}
      data-testid="public-form-root">
      {s.custom_css ? <style dangerouslySetInnerHTML={{ __html: s.custom_css }} /> : null}
      <style>{`
        @media (min-width: 640px) {
          [data-testid="public-form-root"] { padding: 24px !important; }
          .acq-form-card { padding: 32px !important; }
        }
        .acq-input, .acq-textarea, .acq-select {
          width:100%; padding:10px 12px;
          border:1px solid rgba(0,0,0,0.15);
          border-radius: var(--acq-radius);
          background: #fff; color: var(--acq-text);
          font-size: var(--acq-fontsize); font-family: inherit;
          transition: border-color .15s;
          box-sizing: border-box;
        }
        .acq-input:focus, .acq-textarea:focus, .acq-select:focus {
          outline:none; border-color: var(--acq-primary);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--acq-primary) 20%, transparent);
        }
        .acq-label { font-size: 12px; font-weight: 600; margin-bottom: 4px; display:block; }
        .acq-help  { font-size: 11px; opacity: 0.65; margin-top: 3px; }
        .acq-submit {
          background: var(--acq-primary); color:#fff;
          padding: 12px 24px; border: none;
          border-radius: var(--acq-radius);
          font-weight: 600; cursor: pointer;
          font-size: var(--acq-fontsize);
        }
        .acq-submit:hover  { filter: brightness(1.08); }
        .acq-submit:disabled { opacity:.6; cursor: wait; }
        .acq-err {
          color:#DC2626; font-size:12px; margin-top: 4px;
        }
        .acq-honey { position:absolute; left:-9999px; opacity:0; height:0; width:0; }
      `}</style>
      <form onSubmit={submit} className="acq-form-card" style={{ maxWidth: 640, margin: "0 auto", background: "var(--acq-bg)", padding: 20, borderRadius: "var(--acq-radius)", boxShadow: "0 4px 30px rgba(0,0,0,0.05)" }}>
        {s.cover_image_url && <img src={s.cover_image_url} alt="" style={{ width: "100%", height: 160, objectFit: "cover", borderRadius: "var(--acq-radius)", marginBottom: 16 }} />}
        {s.logo_url && <img src={s.logo_url} alt="Logo" style={{ height: 32, marginBottom: 16 }} />}
        {/* R289n · Feb 2026 — Admins can hide the Acquilix title block
            when embedding the form inside their own landing page. */}
        {!form.hide_title && !cfg.hide_title && (
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>
            {cfg.display_title || cfg.title || form.title}
          </h1>
        )}
        {cfg.description && <div style={{ opacity: 0.7, marginBottom: 18 }}>{cfg.description}</div>}
        {cfg.intro_html && <div style={{ marginBottom: 18 }} dangerouslySetInnerHTML={{ __html: cfg.intro_html }} />}

        {(form.fields || []).map((f) => {
          if (!evalVisibleIf(f, values)) return null;
          return <PublicField key={f.id} field={f} value={values[f.id] || ""} onChange={(v) => onChange(f.id, v)} />;
        })}

        {/* Honeypot */}
        <input id="acq-honey" type="text" className="acq-honey" tabIndex="-1" autoComplete="off" />

        {state.error && <div className="acq-err" style={{ marginBottom: 8 }}>{state.error}</div>}

        <button className="acq-submit" type="submit" disabled={state.submitting} data-testid="public-form-submit">
          {state.submitting ? "Submitting…" : (cfg.submit_label || s.button_text || "Submit")}
        </button>
        {s.show_powered_by !== false && (
          <div style={{ marginTop: 24, fontSize: 10, opacity: 0.55, textAlign: "center", letterSpacing: 1 }} data-testid="powered-by">
            Powered by{" "}
            <a href="https://acquilix.com" target="_blank" rel="noopener noreferrer"
                style={{ color: "inherit", textDecoration: "underline", fontWeight: 600 }}>
              Acquilix<sup style={{ fontSize: "0.6em", fontWeight: 400, marginLeft: 1 }}>™</sup>
            </a>
          </div>
        )}
      </form>
    </div>
  );
}

function PublicField({ field, value, onChange }) {
  if (field.kind === "hidden") {
    return <input type="hidden" value={value || field.default_value || ""} readOnly />;
  }
  const req = field.required ? <span style={{ color: "#DC2626" }}> *</span> : null;
  const label = (
    <label className="acq-label">{field.label}{req}</label>
  );
  const wrap = (inner) => (
    <div style={{ marginBottom: 14 }} data-testid={`public-form-field-${field.id}`}>
      {label}
      {inner}
      {field.help_text && field.kind !== "consent" && <div className="acq-help">{field.help_text}</div>}
    </div>
  );
  switch (field.kind) {
    case "textarea":
      return wrap(<textarea className="acq-textarea" rows={4} placeholder={field.placeholder} required={field.required} value={value} onChange={(e) => onChange(e.target.value)} />);
    case "dropdown":
      return wrap(
        <select className="acq-select" required={field.required} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">— select —</option>
          {(field.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    case "radio":
      return wrap(
        <div>
          {(field.options || []).map((o) => (
            <label key={o.value} style={{ display: "block", fontSize: 13, padding: "3px 0" }}>
              <input type="radio" name={field.id} value={o.value} required={field.required} checked={value === o.value} onChange={() => onChange(o.value)} /> {o.label}
            </label>
          ))}
        </div>
      );
    case "checkbox":
      const arr = Array.isArray(value) ? value : [];
      return wrap(
        <div>
          {(field.options || []).map((o) => (
            <label key={o.value} style={{ display: "block", fontSize: 13, padding: "3px 0" }}>
              <input type="checkbox" checked={arr.includes(o.value)}
                onChange={(e) => onChange(e.target.checked ? [...arr, o.value] : arr.filter((x) => x !== o.value))} /> {o.label}
            </label>
          ))}
        </div>
      );
    case "consent":
      return (
        <div style={{ marginBottom: 14 }} data-testid={`public-form-field-${field.id}`}>
          <label style={{ display: "flex", gap: 6, fontSize: 12, alignItems: "flex-start" }}>
            <input type="checkbox" required={field.required} checked={value === true} onChange={(e) => onChange(e.target.checked)} />
            <span>{field.label} {field.help_text}</span>
          </label>
        </div>
      );
    case "signature":
      return wrap(<SignaturePad value={value} onChange={onChange} />);
    case "richtext":
      return (
        <div style={{ marginBottom: 14 }} dangerouslySetInnerHTML={{ __html: field.default_value || "" }} />
      );
    case "file":
      // Feb 2026 · File field uploads to Emergent Object Storage first,
      // then stores the returned URL as the field value so it lands
      // in the CRM lead's extra_data / submission audit trail.
      return wrap(<FileInput field={field} value={value} onChange={onChange} />);
    default: {
      const type = ({ email: "email", phone: "tel", number: "number", date: "date" })[field.kind] || "text";
      return wrap(<input className="acq-input" type={type}
        placeholder={field.placeholder}
        required={field.required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        pattern={field.pattern || undefined}
      />);
    }
  }
}

function FileInput({ field, value, onChange }) {
  const { formId } = useParams();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const meta = (() => { try { return value && typeof value === "object" ? value : (value ? JSON.parse(value) : null); } catch { return null; } })();
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { setErr("File exceeds 10 MB"); return; }
    setBusy(true); setErr(null);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/public/forms/${formId}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ file_id: data.file_id, filename: data.filename, url: data.url, size: data.size, content_type: data.content_type });
    } catch (er) {
      setErr(er?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div>
      {meta ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>📎 {meta.filename} ({Math.round((meta.size || 0) / 1024)} KB)</span>
          <button type="button" onClick={() => onChange("")} style={{ background: "transparent", border: "none", color: "var(--acq-primary)", cursor: "pointer", textDecoration: "underline", fontSize: 12 }}>replace</button>
        </div>
      ) : (
        <input className="acq-input" type="file" onChange={onPick} disabled={busy} />
      )}
      {busy && <div className="acq-help">Uploading…</div>}
      {err && <div className="acq-err">{err}</div>}
    </div>
  );
}


function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const start = (e) => {
    drawing.current = true;
    const rect = canvasRef.current.getBoundingClientRect();
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - rect.top;
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const ctx = canvasRef.current.getContext("2d");
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - rect.top;
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => {
    if (drawing.current) {
      drawing.current = false;
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  };
  const clear = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    onChange("");
  };
  return (
    <div>
      <canvas
        ref={canvasRef}
        width={600} height={140}
        style={{ width: "100%", height: 140, border: "1px solid rgba(0,0,0,0.15)", borderRadius: "var(--acq-radius)", background: "#fff", touchAction: "none" }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <button type="button" onClick={clear} style={{ marginTop: 4, fontSize: 11, background: "transparent", border: "none", color: "var(--acq-primary)", cursor: "pointer", textDecoration: "underline" }}>Clear</button>
    </div>
  );
}

// Feb 2026 · CSS-only confetti burst — 60 particles, deterministic colors
// derived from the form's primary color, ~2s falling animation. Fires
// once on mount then no-ops (parent shows it only on the thank-you view).
function ConfettiBurst({ color = "#EA5934" }) {
  const shades = [color, "#FFD166", "#06D6A0", "#118AB2", "#EF476F"];
  const pieces = Array.from({ length: 80 }, (_, i) => {
    const c = shades[i % shades.length];
    // Deterministic pseudo-random spread using i so SSR/client match.
    const rand = (seed) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };
    const left = Math.round(rand(i + 1) * 100);
    const delay = (rand(i + 2) * 800).toFixed(0);
    const duration = (2200 + rand(i + 3) * 1400).toFixed(0);
    const rotate = Math.round(rand(i + 4) * 720 - 360);
    const size = 6 + Math.round(rand(i + 5) * 8);
    return { c, left, delay, duration, rotate, size, i };
  });
  return (
    <div aria-hidden="true" data-testid="public-form-confetti"
          style={{ position: "fixed", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 1 }}>
      <style>{`
        @keyframes acq-confetti-fall {
          0%   { transform: translateY(-40px) rotate(0deg);   opacity: 0; }
          10%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0.8; }
        }
      `}</style>
      {pieces.map((p) => (
        <span key={p.i}
              style={{
                position: "absolute",
                top: -20, left: `${p.left}%`,
                width: p.size, height: p.size * 0.4,
                background: p.c,
                transform: `rotate(${p.rotate}deg)`,
                animation: `acq-confetti-fall ${p.duration}ms ease-in ${p.delay}ms both`,
                borderRadius: p.i % 3 === 0 ? "50%" : 1,
              }} />
      ))}
    </div>
  );
}

